import { recordFrameVisibility } from '@/lib/analytics/landingPageDwell'

/** Shared IntersectionObserver for dwell tracking. rootMargin is the
 *  measured sticky NavBar height; rebuilt on resize/orientation change. */

type FrameMeta = { frameId: string; frameIndex: number }
const metaByNode = new WeakMap<Element, FrameMeta>()

let observer: IntersectionObserver | null = null
let observedElements: Set<Element> = new Set()
let lastStickyOffset = 0
let resizeHandler: (() => void) | null = null

const THRESHOLDS = [0, 0.1, 0.25, 0.5, 0.75, 1]

/** Picks the first sticky/fixed top:0 element ≥50% viewport-wide
 *  (NavBar) and returns its height. Width filter excludes floating
 *  sticky widgets (notifications, toasts). Returns 0 if none found. */
function measureStickyTopOffset(): number {
    if (typeof document === 'undefined' || typeof window === 'undefined') return 0
    const minWidthForNavBar = window.innerWidth * 0.5
    const candidates = document.body.querySelectorAll('*')
    for (let i = 0; i < candidates.length && i < 200; i++) {
        const el = candidates[i]
        const style = window.getComputedStyle(el)
        if (style.position !== 'sticky' && style.position !== 'fixed') continue
        const rect = el.getBoundingClientRect()
        if (rect.top !== 0 || rect.height === 0 || rect.width < minWidthForNavBar) continue
        return Math.round(rect.height)
    }
    return 0
}

function rebuildObserver(): void {
    if (typeof IntersectionObserver === 'undefined') return
    const offset = measureStickyTopOffset()
    lastStickyOffset = offset
    if (observer) observer.disconnect()
    observer = new IntersectionObserver(handleEntries, {
        threshold: THRESHOLDS,
        rootMargin: offset > 0 ? `-${offset}px 0px 0px 0px` : '0px',
    })
    observedElements.forEach((el) => observer!.observe(el))
}

function handleEntries(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
        const meta = metaByNode.get(entry.target)
        if (!meta) continue
        const targetRect = entry.boundingClientRect
        const intersectRect = entry.intersectionRect
        const rootRect = entry.rootBounds
        if (!rootRect) continue

        // Skip display:none entries. Mantine's hiddenFrom/visibleFrom
        // renders both mobile + desktop stacks; a hidden duplicate would
        // otherwise stop accumulation on the visible sibling sharing
        // the same frame_id.
        if (targetRect.width === 0 && targetRect.height === 0) continue

        const targetHeight = targetRect.height || 1
        const rootHeight = rootRect.height || 1
        const visibleFraction = Math.min(1, Math.max(0, intersectRect.height / targetHeight))
        const viewportCoverage = Math.min(1, Math.max(0, intersectRect.height / rootHeight))
        const scrolledPast = targetRect.bottom <= rootRect.top + 1

        recordFrameVisibility(meta.frameId, meta.frameIndex, {
            visibleFraction,
            viewportCoverage,
            scrolledPast,
        })
    }
}

export function observeFrame(el: Element, meta: FrameMeta): void {
    metaByNode.set(el, meta)
    observedElements.add(el)
    if (!observer) rebuildObserver()
    observer?.observe(el)
}

export function unobserveFrame(el: Element): void {
    observedElements.delete(el)
    metaByNode.delete(el)
    observer?.unobserve(el)
}

export function startObserverLifecycle(): void {
    if (typeof window === 'undefined') return
    if (resizeHandler) return
    resizeHandler = () => {
        // Only rebuild if the offset changed (avoids resize-spam churn).
        const next = measureStickyTopOffset()
        if (next === lastStickyOffset) return
        rebuildObserver()
    }
    window.addEventListener('resize', resizeHandler)
    window.addEventListener('orientationchange', resizeHandler)
}

export function stopObserverLifecycle(): void {
    if (typeof window === 'undefined') return
    if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler)
        window.removeEventListener('orientationchange', resizeHandler)
        resizeHandler = null
    }
    if (observer) {
        observer.disconnect()
        observer = null
    }
    observedElements = new Set()
}
